import { ViewController } from '@estee/elc-service';
import { observable, action, computed } from 'mobx';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { FiltersRepository } from '~repositories/FiltersRepository';
import { FilterToggledUseCase } from '~use-cases/FilterToggledUseCase';
import { Config } from '~domain/entities/Config';
import { HydrateStoresWithCountersUseCase } from '~use-cases/HydrateStoresWithCountersUseCase';
import { events, emit } from '@estee/elc-service-bus';
import { BooleanFilterTypes } from '~constants/constants';

export class StoresFiltersViewController extends ViewController {
    @observable private isFiltersListExpanded: boolean = false;
    @observable private isFilterCategoryExpanded: boolean = true;
    @observable private filtersRepository: FiltersRepository;
    @observable private configService: Config;

    constructor() {
        super();
        this.filtersRepository = diContainer.get(serviceNames.filtersRepository);
        this.configService = diContainer.get(serviceNames.config);
    }

    @action
    public onFiltersListClick = (filterName?: string) => {
        this.isFiltersListExpanded = !this.isFiltersListExpanded;
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: this.isFiltersListExpanded ? 'expand' : 'collapse',
            eventLabel: `${filterName}`
        });
    };

    @action
    public onFiltersCategoryClick = (filterName?: string) => {
        this.isFilterCategoryExpanded = !this.isFilterCategoryExpanded;
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: this.isFilterCategoryExpanded ? 'expand' : 'collapse',
            eventLabel: `${filterName}`
        });
    };

    @action
    public onFilterClick = async (_: boolean, value: string) => {
        this.filtersRepository.toggleMultiOptionFilter(value);
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: _ ? 'select' : 'deselect',
            eventLabel: value
        });
        await new FilterToggledUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };

    @action
    public onOpenNowClick = async (toggled?: boolean) => {
        this.filtersRepository.toggleBooleanFilter(BooleanFilterTypes.OPEN_NOW);
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: toggled ? 'select' : 'deselect',
            eventLabel: 'Open Now'
        });
        await new FilterToggledUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };

    @action
    public onServicesAvailableClick = async (toggled?: boolean) => {
        this.filtersRepository.toggleBooleanFilter(BooleanFilterTypes.OAB_SERVICES);
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: toggled ? 'select' : 'deselect',
            eventLabel: 'Services available'
        });
        await new FilterToggledUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };

    @action
    public onInStorePickupClick = async (toggled?: boolean) => {
        this.filtersRepository.toggleBooleanFilter(BooleanFilterTypes.IN_STORE_PICKUP);
        emit(events.STORE_FILTER_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_filters',
            eventAction: toggled ? 'select' : 'deselect',
            eventLabel: 'In Store Pickup'
        });
        await new FilterToggledUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };

    @computed
    private get numberOfActiveFilters() {
        const { booleanFilters, multiOptionFilters, servicesAvailableFilter, inStorePickupFilter } =
            this.filtersRepository;
        const activeBooleanFiltersCount = Object.values(booleanFilters).filter(Boolean).length;

        const activeMultiOptionFiltersCount = Object.values(multiOptionFilters).reduce(
            (counter, filters) => counter + filters.length,
            0
        );

        return (
            activeBooleanFiltersCount +
            activeMultiOptionFiltersCount +
            Number(servicesAvailableFilter) +
            Number(inStorePickupFilter)
        );
    }

    @computed
    public get data() {
        return {
            isFiltersListExpanded: this.isFiltersListExpanded,
            isFiltersCategoryExpanded: this.isFilterCategoryExpanded,
            numberOfActiveFilters: this.numberOfActiveFilters,
            availableFilters: this.filtersRepository.availableFilters,
            multiOptionFilters: this.filtersRepository.multiOptionFilters,
            booleanFilters: this.filtersRepository.booleanFilters,
            filtersEnabled: this.filtersRepository.filtersEnabled,
            openNowEnabled: this.filtersRepository.openNowEnabled,
            isOAB: this.configService.config.isOAB
        };
    }
}
